import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import './MasterData.css';

interface Vendor {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: string;
  tin: string;
  contactPerson: string;
  email: string;
  phone: string;
  city: string;
  paymentTermDays: number;
  isActive: string;
}

const Vendors: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  useEffect(() => {
    // Mock data
    setVendors([
      {
        vendorId: 1,
        vendorCode: 'VEND001',
        vendorName: 'Pacific Lumber Inc.',
        vendorType: 'RAW_MATERIAL',
        tin: '234-567-890-000',
        contactPerson: 'Juan Dela Cruz',
        email: 'sales@pacificlumber.ph',
        phone: '+63 2 3456 7890',
        city: 'Cebu',
        paymentTermDays: 30,
        isActive: 'Y'
      },
      {
        vendorId: 2,
        vendorCode: 'VEND002',
        vendorName: 'Manila Fabric Suppliers',
        vendorType: 'RAW_MATERIAL',
        tin: '345-678-901-000',
        contactPerson: 'Maria Santos',
        email: 'info@manilafabric.ph',
        phone: '+63 2 4567 8901',
        city: 'Manila',
        paymentTermDays: 15,
        isActive: 'Y'
      },
      {
        vendorId: 3,
        vendorCode: 'VEND003',
        vendorName: 'Hardware Solutions Co.',
        vendorType: 'RAW_MATERIAL',
        tin: '456-789-012-000',
        contactPerson: 'Pedro Garcia',
        email: 'sales@hardwaresolutions.ph',
        phone: '+63 2 5678 9012',
        city: 'Makati',
        paymentTermDays: 30,
        isActive: 'Y'
      }
    ]);
  }, []);

  const filteredVendors = vendors.filter(vendor =>
    vendor.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.vendorCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Vendors</h2>
            <p className="subtitle">Manage supplier and vendor information</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingVendor(null); setShowModal(true); }}>
            <MdAdd /> Add New Vendor
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Vendor Name</th>
                  <th>Type</th>
                  <th>TIN</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Payment Terms</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map(vendor => (
                  <tr key={vendor.vendorId}>
                    <td className="code">{vendor.vendorCode}</td>
                    <td className="name">{vendor.vendorName}</td>
                    <td>
                      <span className="badge badge-purple">
                        {vendor.vendorType.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{vendor.tin}</td>
                    <td>{vendor.contactPerson}</td>
                    <td>{vendor.email}</td>
                    <td>{vendor.phone}</td>
                    <td>{vendor.city}</td>
                    <td>{vendor.paymentTermDays} days</td>
                    <td>
                      <span className={`badge ${vendor.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                        {vendor.isActive === 'Y' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => { setEditingVendor(vendor); setShowModal(true); }}>
                          <MdEdit />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => {
                          if (window.confirm('Delete this vendor?')) {
                            setVendors(vendors.filter(v => v.vendorId !== vendor.vendorId));
                          }
                        }}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <VendorModal
          vendor={editingVendor}
          onClose={() => setShowModal(false)}
          onSave={(vendor) => {
            if (editingVendor) {
              setVendors(vendors.map(v => v.vendorId === vendor.vendorId ? vendor : v));
            } else {
              setVendors([...vendors, { ...vendor, vendorId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface VendorModalProps {
  vendor: Vendor | null;
  onClose: () => void;
  onSave: (vendor: Vendor) => void;
}

const VendorModal: React.FC<VendorModalProps> = ({ vendor, onClose, onSave }) => {
  const [formData, setFormData] = useState<Vendor>(
    vendor || {
      vendorId: 0,
      vendorCode: '',
      vendorName: '',
      vendorType: 'RAW_MATERIAL',
      tin: '',
      contactPerson: '',
      email: '',
      phone: '',
      city: '',
      paymentTermDays: 30,
      isActive: 'Y'
    }
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Vendor Code *</label>
                <input type="text" required value={formData.vendorCode}
                  onChange={(e) => setFormData({ ...formData, vendorCode: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Vendor Name *</label>
                <input type="text" required value={formData.vendorName}
                  onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select value={formData.vendorType}
                  onChange={(e) => setFormData({ ...formData, vendorType: e.target.value })}>
                  <option value="RAW_MATERIAL">Raw Material</option>
                  <option value="SERVICE">Service</option>
                  <option value="OUTSOURCE">Outsource</option>
                </select>
              </div>
              <div className="form-group">
                <label>TIN *</label>
                <input type="text" required value={formData.tin}
                  onChange={(e) => setFormData({ ...formData, tin: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Contact Person</label>
                <input type="text" value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="text" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>City</label>
                <input type="text" value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Payment Terms (days)</label>
                <input type="number" value={formData.paymentTermDays}
                  onChange={(e) => setFormData({ ...formData, paymentTermDays: parseInt(e.target.value) })} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}>
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{vendor ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Vendors;
